import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Building2, RefreshCcw } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

// Sprint DASHBOARD — una sola pagina: ogni blocco compare solo se l'endpoint lo restituisce
// (condizioni e ambito decisi lato server in dashboard_api.py).
const eur = (n) => `€ ${Number(n).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;
const GRAVITA = {
  critico: { dot: "bg-rose-600", pill: "bg-rose-50 text-rose-700 border-rose-200" },
  scadenza: { dot: "bg-amber-600", pill: "bg-amber-50 text-amber-800 border-amber-200" },
  attesa: { dot: "bg-violet-600", pill: "bg-violet-50 text-violet-700 border-violet-200" },
  informativo: { dot: "bg-blue-600", pill: "bg-blue-50 text-blue-700 border-blue-200" },
};
const DOVE = [["AVAILABLE", "Disponibili in sede"], ["INCOMING", "In arrivo"], ["VIRTUAL", "Virtuali"]];
const CARICO = [["conto_terzi", "Conto terzi (mandato)"], ["conto_proprio", "Conto proprio"]];

const Box = ({ children, className = "", testid }) => (
  <section className={`bg-white border border-slate-200 rounded-xl p-5 ${className}`} data-testid={testid}>{children}</section>
);

function Riquadro({ titolo, valore, sotto, testid }) {
  return (
    <Box testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{titolo}</div>
      <div className="font-heading text-3xl font-semibold text-slate-900 mt-1 tabular-nums">{valore}</div>
      {sotto && <div className="text-xs text-slate-500 mt-1">{sotto}</div>}
    </Box>
  );
}

function Riquadri({ r }) {
  const s = r.stock;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <Riquadro testid="dash-stock" titolo="Veicoli in stock" valore={s.totale}
        sotto={"valore_carico" in s ? `Valore di carico ${eur(s.valore_carico)}` : `${s.disponibili_in_sede} disponibili in sede`} />
      <Riquadro testid="dash-pratiche-aperte" titolo={r.pratiche_aperte.etichetta} valore={r.pratiche_aperte.totale}
        sotto={`${r.pratiche_aperte.impegnate} impegnate · ${r.pratiche_aperte.vendute_da_consegnare} vendute da consegnare`} />
      <Riquadro testid="dash-da-incassare" titolo={r.da_incassare.etichetta} valore={eur(r.da_incassare.importo)}
        sotto={`su ${r.da_incassare.n_pratiche} pratiche`} />
      <Riquadro testid="dash-sito" titolo="Pubblicate sul sito" valore={`${r.sito.pubblicate} su ${r.sito.totale}`}
        sotto={`${r.sito.totale - r.sito.pubblicate} non ancora pubblicate`} />
    </div>
  );
}

function Attenzione({ righe }) {
  const navigate = useNavigate();
  return (
    <Box testid="dash-attenzione">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="font-heading text-base font-semibold text-slate-900">Richiede attenzione</h2>
        <span className="text-xs text-slate-500">Ogni riga apre l'elenco corrispondente</span>
      </div>
      {righe.length === 0 ? (
        <div className="text-sm text-slate-500 py-2" data-testid="dash-attenzione-vuoto">Nessuna segnalazione.</div>
      ) : righe.map((x) => (
        <button key={x.chiave} type="button" onClick={() => navigate(x.link)} data-testid={`dash-att-${x.chiave}`}
          className="w-full flex items-center gap-3 py-2.5 border-t first:border-t-0 border-slate-100 text-left hover:bg-slate-50 rounded-md px-1 transition-colors">
          <span className={`h-2 w-2 rounded-full shrink-0 ${GRAVITA[x.gravita].dot}`} />
          <span className="flex-1 text-sm text-slate-800">{x.testo}</span>
          {x.nota && <span className="text-xs text-slate-500 hidden sm:inline">{x.nota}</span>}
          <span className={`min-w-[28px] text-center text-xs font-semibold border rounded-md px-1.5 py-0.5 ${GRAVITA[x.gravita].pill}`}>{x.conteggio}</span>
          <ChevronRight className="h-4 w-4 text-slate-400" />
        </button>
      ))}
    </Box>
  );
}

function VenditeMese({ v, onUtente }) {
  const max = Math.max(1, ...v.mesi.map((m) => m.n));
  return (
    <Box className="lg:col-span-2" testid="dash-vendite-mese">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-heading text-base font-semibold text-slate-900">{v.etichetta}</h2>
        <div className="flex items-center gap-3">
          {("margine_mese" in v || v.margine_esclusi) && (
            <span className="text-xs text-slate-500" data-testid="dash-margine-mese">
              Margine del mese {"margine_mese" in v ? <b className="text-slate-900">{eur(v.margine_mese)}</b> : "non calcolabile"}
              {v.margine_esclusi && (
                <span className="text-[11px] text-amber-700" data-testid="dash-margine-esclusi">
                  {" · "}{v.margine_esclusi.n} {v.margine_esclusi.n === 1 ? "pratica esclusa" : "pratiche escluse"}, {v.margine_esclusi.motivo}
                </span>
              )}
            </span>
          )}
          {v.utenti && (
            <select value={v.utente_selezionato || ""} onChange={(e) => onUtente(e.target.value || null)}
              className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="dash-filtro-utente">
              <option value="">Tutti gli utenti</option>
              {v.utenti.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
            </select>
          )}
        </div>
      </div>
      <div className="flex items-end gap-4 h-48">
        {v.mesi.map((m, i) => (
          <div key={m.mese} className="flex-1 flex flex-col items-center justify-end h-full" data-testid={`dash-mese-${m.mese}`}>
            <span className="text-xs text-slate-700 mb-1 tabular-nums">{m.n}</span>
            <div className={`w-full max-w-[56px] rounded-t-md ${i === v.mesi.length - 1 ? "bg-blue-600" : "bg-blue-200"}`}
              style={{ height: `${(m.n / max) * 80}%`, minHeight: m.n ? 4 : 0 }} />
            <span className="text-xs text-slate-500 mt-2">{m.label}</span>
          </div>
        ))}
      </div>
    </Box>
  );
}

function Barre({ titolo, voci, valori }) {
  const max = Math.max(1, ...voci.map(([k]) => valori[k] || 0));
  return (
    <div className="mb-4 last:mb-0">
      <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">{titolo}</div>
      {voci.map(([k, label]) => (
        <div key={k} className="mb-2.5" data-testid={`dash-comp-${k}`}>
          <div className="flex justify-between text-sm text-slate-700"><span>{label}</span><span className="tabular-nums">{valori[k] || 0}</span></div>
          <div className="h-1.5 bg-slate-100 rounded-full mt-1">
            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${((valori[k] || 0) / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Composizione({ c }) {
  return (
    <Box testid="dash-composizione">
      <h2 className="font-heading text-base font-semibold text-slate-900 mb-3">Com'è composto lo stock</h2>
      <Barre titolo="Dove sono" voci={DOVE} valori={c.dove} />
      <Barre titolo="Come sono caricate" voci={CARICO} valori={c.carico} />
    </Box>
  );
}

function Confronto({ righe }) {
  return (
    <Box testid="dash-confronto">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-heading text-base font-semibold text-slate-900">Confronto per azienda</h2>
        <span className="text-xs text-slate-500">Compare quando l'ambito comprende più aziende</span>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-[11px] uppercase text-slate-500 text-right">
          <th className="text-left py-1.5 font-medium">Azienda</th><th className="font-medium">In stock</th>
          <th className="font-medium">Pratiche aperte</th><th className="font-medium">Da incassare</th><th className="font-medium">Sul sito</th>
        </tr></thead>
        <tbody>{righe.map((r) => (
          <tr key={r.company_id} className="border-t border-slate-100 text-right tabular-nums" data-testid={`dash-confronto-${r.company_id}`}>
            <td className="text-left py-2.5">{r.azienda}</td><td>{r.in_stock}</td><td>{r.pratiche_aperte}</td>
            <td>{eur(r.da_incassare)}</td><td>{r.sul_sito} su {r.in_stock}</td>
          </tr>
        ))}</tbody>
      </table>
    </Box>
  );
}

const quando = (iso) => {
  const d = new Date(iso);
  const oggi = new Date();
  const ieri = new Date(Date.now() - 864e5);
  const ora = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === oggi.toDateString()) return `Oggi ${ora}`;
  if (d.toDateString() === ieri.toDateString()) return `Ieri ${ora}`;
  return `${d.toLocaleDateString("it-IT", { day: "numeric", month: "short" })} ${ora}`;
};

function Attivita({ a }) {
  return (
    <Box testid="dash-attivita">
      <h2 className="font-heading text-base font-semibold text-slate-900 mb-2">{a.etichetta}</h2>
      {a.righe.length === 0 ? <div className="text-sm text-slate-500">Nessuna attività registrata.</div> : a.righe.map((r, i) => (
        <div key={i} className="flex gap-4 py-2 border-t first:border-t-0 border-slate-100 text-sm" data-testid={`dash-attivita-${i}`}>
          <span className="w-28 shrink-0 text-xs text-slate-500 pt-0.5">{quando(r.quando)}</span>
          <span className="flex-1 text-slate-800">{r.cosa}</span>
          {r.chi && <span className="text-xs text-slate-500">{r.chi}</span>}
        </div>
      ))}
    </Box>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [errore, setErrore] = useState(false);
  const [utente, setUtente] = useState(null);
  const [ora, setOra] = useState(null);
  const carica = useCallback(() => {
    setErrore(false);
    api.get("/dashboard", { params: utente ? { utente_id: utente } : {} })
      .then((r) => { setD(r.data); setOra(new Date()); })
      .catch(() => setErrore(true));
  }, [utente]);
  useEffect(() => { carica(); }, [carica]);

  if (errore) {
    return (
      <div className="bg-white border border-rose-200 rounded-xl p-6 text-sm text-rose-800" data-testid="dash-errore">
        Non è stato possibile caricare la dashboard: i dati non sono disponibili in questo momento.
        <Button variant="outline" size="sm" className="ml-3" onClick={carica} data-testid="dash-riprova"><RefreshCcw className="h-4 w-4 mr-1" /> Riprova</Button>
      </div>
    );
  }
  if (!d) return <div className="text-slate-500" data-testid="dash-caricamento">Caricamento…</div>;
  return (
    <div className="space-y-5" data-testid="dashboard-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-xs text-slate-500 mt-1">
            {ora.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            {" · aggiornato alle "}{ora.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
          </p>
        </div>
        <div className="inline-flex items-center gap-2 text-xs text-slate-500 border border-slate-200 bg-white rounded-lg px-3 py-2" data-testid="dash-ambito">
          <Building2 className="h-3.5 w-3.5" /> Stai vedendo <b className="text-slate-900">{d.ambito.etichetta}</b>
        </div>
      </div>
      <Riquadri r={d.riquadri} />
      <Attenzione righe={d.attenzione} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <VenditeMese v={d.vendite_mese} onUtente={setUtente} />
        <Composizione c={d.composizione} />
      </div>
      {d.confronto_aziende && <Confronto righe={d.confronto_aziende} />}
      <Attivita a={d.attivita} />
    </div>
  );
}
